import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Bell, BellOff, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { cldImage } from "@/lib/cldImage";
import { fmtIST } from "@/lib/formatIST";
import { useAdminEventPoller } from "@/hooks/useAdminEventPoller";
import { unlock as unlockAdminSounds } from "@/lib/adminSounds";
import ThermalReceipt from "@/components/admin/ThermalReceipt";
import CancelOrderModal from "@/components/admin/CancelOrderModal";
import CancellationStats from "@/components/admin/CancellationStats";
import StorePanel from "@/components/admin/StorePanel";
import CustomerInsights from "@/components/admin/CustomerInsights";
import AccountsPanel from "@/components/admin/AccountsPanel";
import SyncGoogleReviewsBtn from "@/components/admin/SyncGoogleReviewsBtn";
import InventoryPanel from "@/components/admin/InventoryPanel";
import MarginAnalytics from "@/components/admin/MarginAnalytics";
import MarginManagement from "@/components/admin/MarginManagement";
import DeleteOrderModal from "@/components/admin/DeleteOrderModal";
import OrderDetailsModal from "@/components/admin/OrderDetailsModal";

const STATUSES_D = ["received","confirmed","preparing","ready","out_for_delivery","delivered"];
const STATUSES_P = ["received","confirmed","preparing","ready_pickup","collected"];
const ORDER_DELETE_OWNER_EMAIL = "akgautam430@gmail.com";

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [alertsOn, setAlertsOn] = useState(() => {
    try { return localStorage.getItem("admin-alerts") === "1"; } catch { return false; }
  });
  useAdminEventPoller(alertsOn);

  const toggleAlerts = () => {
    const next = !alertsOn;
    if (next) {
      const ok = unlockAdminSounds();
      if (!ok) { toast.error("Your browser blocked audio. Try clicking again."); return; }
      toast.success("Alerts on — you'll hear a chime for new activity.");
    } else {
      toast.info("Alerts off.");
    }
    setAlertsOn(next);
    try { localStorage.setItem("admin-alerts", next ? "1" : "0"); } catch (_) {}
  };

  useEffect(() => {
    // Hard block: only the boutique owner (is_admin=true) can view the
    // dashboard. Anyone else — even a signed-in customer — is bounced to
    // the home page. Unauthenticated visitors go to /admin to sign in.
    if (loading) return;
    if (!user) { navigate("/admin", { replace: true }); return; }
    if (!user.is_admin) navigate("/", { replace: true });
  }, [user, loading, navigate]);

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center text-espresso-light label-xs">Loading…</div>;

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-ivory">
      <header className="border-b border-line bg-ivory">
        <div className="max-w-[1400px] mx-auto px-4 md:px-10 h-16 flex items-center justify-between gap-3">
          <div className="font-serif text-base sm:text-xl tracking-[0.06em] text-espresso font-semibold truncate">
            TIRAMISÙ <span className="text-gold">&amp;</span> Co. · Admin
          </div>
          <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0">
            <SyncGoogleReviewsBtn />
            <button
              onClick={toggleAlerts}
              data-testid="admin-alerts-toggle"
              aria-pressed={alertsOn}
              title={alertsOn ? "Sound alerts on — click to mute" : "Sound alerts off — click to enable"}
              className={`label-xs inline-flex items-center gap-2 px-3 py-2 rounded-full border transition-colors ${
                alertsOn
                  ? "border-gold text-gold-hover bg-gold/10"
                  : "border-line text-espresso-light hover:text-espresso"
              }`}
            >
              {alertsOn ? <Bell size={14} strokeWidth={1.75} /> : <BellOff size={14} strokeWidth={1.75} />}
              <span className="hidden sm:inline">{alertsOn ? "Alerts on" : "Alerts off"}</span>
            </button>
            <span className="text-xs text-espresso-light hidden md:inline max-w-[180px] truncate">{user.email}</span>
            <button onClick={() => { logout(); navigate("/admin"); }} className="label-xs text-espresso-light hover:text-espresso py-2" data-testid="admin-logout">Sign out</button>
            <button
              onClick={() => navigate("/admin/referrals")}
              data-testid="admin-referrals-link"
              className="label-xs border border-line px-3 py-2 text-espresso-light transition-colors hover:border-espresso hover:text-espresso"
            >
              Referrals
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 md:px-10 py-6 md:py-10">
        <Tabs defaultValue="orders">
          <TabsList
            className={
              "flex h-auto w-full flex-wrap justify-start gap-1 "
              + "overflow-hidden bg-ivory-100 p-1"
            }
          >
            <TabsTrigger value="orders" data-testid="tab-orders" className="whitespace-nowrap">Orders</TabsTrigger>
            <TabsTrigger value="store" data-testid="tab-store" className="whitespace-nowrap">Store</TabsTrigger>
            <TabsTrigger value="bulk" data-testid="tab-bulk" className="whitespace-nowrap">Bulk requests</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews" className="whitespace-nowrap">Reviews</TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products" className="whitespace-nowrap">Products</TabsTrigger>
            <TabsTrigger value="inventory" data-testid="tab-inventory" className="whitespace-nowrap">Inventory</TabsTrigger>
            <TabsTrigger value="timings" data-testid="tab-timings" className="whitespace-nowrap">Timings</TabsTrigger>
            <TabsTrigger value="promos" data-testid="tab-promos" className="whitespace-nowrap">Promotions</TabsTrigger>
            <TabsTrigger value="loyalty" data-testid="tab-loyalty" className="whitespace-nowrap">Loyalty</TabsTrigger>
            <TabsTrigger value="customers" data-testid="tab-customers" className="whitespace-nowrap">Customers</TabsTrigger>
            <TabsTrigger value="accounts" data-testid="tab-accounts" className="whitespace-nowrap">Accounts</TabsTrigger>
            <TabsTrigger value="margin-analytics" data-testid="tab-margin-analytics" className="whitespace-nowrap text-gold-hover">Margin Analytics</TabsTrigger>
            <TabsTrigger value="margins" data-testid="tab-margins" className="whitespace-nowrap text-gold-hover">Margin Management</TabsTrigger>
            <TabsTrigger value="stats" data-testid="tab-stats" className="whitespace-nowrap">Overview</TabsTrigger>
          </TabsList>

          <TabsContent value="stats"><StatsPanel /></TabsContent>
          <TabsContent value="orders">
            <OrdersPanel
              canDeleteOrders={
                user.email?.trim().toLowerCase() === ORDER_DELETE_OWNER_EMAIL
              }
            />
          </TabsContent>
          <TabsContent value="store"><StorePanel /></TabsContent>
          <TabsContent value="bulk"><BulkPanel /></TabsContent>
          <TabsContent value="reviews"><ReviewsPanel /></TabsContent>
          <TabsContent value="products"><ProductsPanel /></TabsContent>
          <TabsContent value="inventory"><InventoryPanel /></TabsContent>
          <TabsContent value="timings"><TimingsPanel /></TabsContent>
          <TabsContent value="promos"><PromosPanel /></TabsContent>
          <TabsContent value="loyalty"><LoyaltyPanel /></TabsContent>
          <TabsContent value="customers"><CustomerInsights /></TabsContent>
          <TabsContent value="accounts"><AccountsPanel /></TabsContent>
          <TabsContent value="margin-analytics"><MarginAnalytics /></TabsContent>
          <TabsContent value="margins"><MarginManagement /></TabsContent>
        </Tabs>
      </div>
    </main>
  );
}

function StatsPanel() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setS(data)); }, []);
  if (!s) return <div className="mt-10 label-xs">Loading…</div>;
  const items = [
    ["Total orders", s.total_orders], ["Today", s.today_orders],
    ["Revenue (paid)", `₹${s.revenue}`], ["Products", s.products],
  ];
  return (
    <div className="mt-8 md:mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
      {items.map(([k, v]) => (
        <div key={k} className="border border-line p-5 md:p-8 bg-ivory">
          <div className="label-xs text-espresso-light">{k}</div>
          <div className="font-serif text-4xl text-espresso mt-3">{v}</div>
        </div>
      ))}
    </div>
  );
}

function OrdersPanel({ canDeleteOrders }) {
  const [orders, setOrders] = useState([]);
  const [paymentCfg, setPaymentCfg] = useState(null);
  const [filter, setFilter] = useState("");
  const [printingOrder, setPrintingOrder] = useState(null);
  const [detailOrder, setDetailOrder] = useState(null);
  const [cancelingOrder, setCancelingOrder] = useState(null);
  const [deletingOrder, setDeletingOrder] = useState(null);
  const load = () => api.get("/admin/orders").then(({ data }) => setOrders(data));
  useEffect(() => {
    load();
    api.get("/config/payment").then(({ data }) => setPaymentCfg(data)).catch(() => {});
  }, []);
  const setStatus = async (id, status) => {
    await api.put(`/admin/orders/${id}/status`, { status });
    toast.success("Status updated");
    load();
  };
  const sendReviewRequest = async (o) => {
    try {
      const { data } = await api.post(`/admin/orders/${o.id}/review-request`);
      toast.success("Review request sent");
      if (data.review_url && navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(data.review_url);
          toast.success("Link copied to clipboard");
        } catch (_) {}
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send review request");
    }
  };

  const refundOrder = async (o) => {
    const reason = window.prompt(`Refund ₹${o.total.toFixed(0)} for ${o.order_number}? Reason (optional):`, "");
    if (reason === null) return; // cancelled
    try {
      await api.post(`/admin/orders/${o.id}/refund`, { notes: reason || null });
      toast.success("Refund processed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refund failed");
    }
  };
  const updateOrderFromDetail = (updatedOrder) => {
    setOrders((current) => current.map((order) => (
      order.id === updatedOrder.id ? updatedOrder : order
    )));
    setDetailOrder(updatedOrder);
  };
  const shown = filter ? orders.filter((o) => o.status === filter || o.fulfillment === filter) : orders;
  return (
    <div className="mt-8">
      <CancellationStats />
      <div className="flex flex-wrap gap-2 mb-6">
        {["","received","confirmed","preparing","ready","out_for_delivery","delivered","delivery","pickup"].map((f) => (
          <button key={f||"all"} onClick={() => setFilter(f)} data-testid={`orders-filter-${f||"all"}`}
            className={`label-xs px-3 py-1 rounded-full border ${filter===f?"bg-espresso text-ivory border-espresso":"border-line text-espresso-light"}`}>
            {f || "All"}
          </button>
        ))}
      </div>
      <div className="space-y-4">
        {shown.map((o) => {
          const statuses = o.fulfillment === "pickup" ? STATUSES_P : STATUSES_D;
          return (
            <div key={o.id} className="border border-line p-6 bg-ivory">
              <div className="grid md:grid-cols-5 gap-4">
                <div>
                  <div className="label-xs text-espresso-light">Order</div>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <div
                      className="font-serif text-xl text-espresso"
                      data-testid={`order-number-${o.order_number}`}
                    >
                      {o.order_number}
                    </div>
                    {o.is_preorder && (
                      <span
                        className="border border-gold bg-gold/10 px-2 py-1 label-xs text-espresso"
                        data-testid={`preorder-badge-${o.order_number}`}
                      >
                        PRE-ORDER
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-espresso-light mt-1">{fmtIST(o.created_at)}</div>
                </div>
                <div>
                  <div className="label-xs text-espresso-light">Customer</div>
                  <div className="text-sm text-espresso mt-1">{o.customer.full_name}</div>
                  <div className="text-xs text-espresso-light">{o.customer.mobile}</div>
                </div>
                <div
                  className="border-l-2 border-gold bg-gold/10 px-3 py-2"
                  data-testid={`admin-order-slot-${o.order_number}`}
                >
                  <div className="label-xs text-gold-hover">
                    Chosen {o.fulfillment} slot
                  </div>
                  <div
                    className="mt-1 font-serif text-xl text-espresso"
                    data-testid={`admin-order-slot-value-${o.order_number}`}
                  >
                    {o.slot_date} · {o.slot_time}
                  </div>
                  {o.fulfillment === "delivery" && (
                    <div className="mt-1 max-w-[220px] truncate text-xs text-espresso-light">
                      {o.customer.address_line1}, {o.customer.pincode}
                    </div>
                  )}
                </div>
                <div>
                  <div className="label-xs text-espresso-light">Total · Payment</div>
                  <div className="text-sm text-espresso mt-1">₹{o.total.toFixed(0)} · <span className="capitalize">{o.payment_status}</span></div>
                  {/* Order summary breakdown — subtotal + GST + delivery.
                      Matches the invoice PDF and thermal receipt so admin
                      can eyeball reconciliation without opening either. */}
                  <div className="mt-2 text-[11px] text-espresso-light leading-relaxed space-y-0.5" data-testid={`order-breakdown-${o.order_number}`}>
                    <div className="flex justify-between"><span>Subtotal</span><span>₹{Number(o.subtotal || 0).toFixed(0)}</span></div>
                    {o.discount > 0 && (
                      <div className="flex justify-between"><span>Discount{o.promo_code ? ` (${o.promo_code})` : ""}</span><span>−₹{Number(o.discount).toFixed(0)}</span></div>
                    )}
                    <div className="flex justify-between"><span>CGST (2.5%)</span><span>₹{Number(o.cgst || 0).toFixed(2)}</span></div>
                    <div className="flex justify-between"><span>SGST (2.5%)</span><span>₹{Number(o.sgst || 0).toFixed(2)}</span></div>
                    {o.delivery_fee > 0 && (
                      <div className="flex justify-between"><span>Delivery</span><span>₹{Number(o.delivery_fee).toFixed(0)}</span></div>
                    )}
                  </div>
                </div>
                <div>
                  <div className="label-xs text-espresso-light">Status</div>
                  <select value={o.status} onChange={(e) => setStatus(o.id, e.target.value)}
                    data-testid={`status-select-${o.order_number}`}
                    className="mt-1 w-full bg-transparent border border-line px-2 py-1 text-sm text-espresso">
                    {statuses.map((s) => <option key={s} value={s}>{s.replace("_"," ")}</option>)}
                  </select>
                </div>
              </div>
              <div className="mt-4 text-xs text-espresso-light">
                {o.items.map((it, i) => (
                  <span key={i} className="mr-4">{it.name}{it.flavor?` · ${it.flavor}`:""} × {it.quantity}</span>
                ))}
              </div>
              {o.profit && (
                <div
                  className="mt-4 border-l-2 border-emerald-700 bg-emerald-50/60 px-4 py-3"
                  data-testid={`order-profit-${o.order_number}`}
                >
                  <div className="label-xs text-emerald-800">Order profit · website sales only</div>
                  <div
                    className={
                      "mt-2 grid gap-x-5 gap-y-1 text-xs text-espresso-light "
                      + "sm:grid-cols-3 lg:grid-cols-7"
                    }
                  >
                    <div>
                      <span>Revenue</span>
                      <strong className="ml-1 text-espresso" data-testid={`profit-revenue-${o.order_number}`}>
                        ₹{Number(o.profit.revenue || 0).toFixed(2)}
                      </strong>
                    </div>
                    <div>
                      <span>Gateway fee · 3%</span>
                      <strong className="ml-1 text-amber-800" data-testid={`profit-gateway-${o.order_number}`}>
                        −₹{Number(o.profit.gateway_fee || 0).toFixed(2)}
                      </strong>
                    </div>
                    <div>
                      <span>Total COGS</span>
                      <strong className="ml-1 text-rose-700" data-testid={`profit-cogs-${o.order_number}`}>
                        −₹{Number(o.profit.total_cogs || 0).toFixed(2)}
                      </strong>
                    </div>
                    <div>
                      <span>Charged Customer</span>
                      <strong
                        className="ml-1 text-espresso"
                        data-testid={`profit-charged-customer-${o.order_number}`}
                      >
                        ₹{Number(o.profit.charged_customer_delivery_fee || 0).toFixed(2)}
                      </strong>
                    </div>
                    {Number(o.profit.delivery_fee_adjustment || 0) !== 0 && (
                      <div>
                        <span>Delivery fee adjustment</span>
                        <strong
                          className="ml-1 text-emerald-800"
                          data-testid={`profit-delivery-adjustment-${o.order_number}`}
                        >
                          {o.profit.delivery_fee_adjustment > 0 ? "+" : "−"}₹
                          {Math.abs(Number(o.profit.delivery_fee_adjustment)).toFixed(2)}
                        </strong>
                      </div>
                    )}
                    <div>
                      <span>
                        Estimated Actual Cost{ o.profit.actual_delivery_cost_is_manual ? " · manual" : ""}
                      </span>
                      <strong
                        className="ml-1 text-sky-800"
                        data-testid={`profit-delivery-${o.order_number}`}
                      >
                        {o.profit.actual_delivery_cost_available
                          ? `−₹${Number(o.profit.actual_delivery_cost).toFixed(2)}`
                          : "Road route pending"}
                      </strong>
                    </div>
                    {o.profit.launch_cookie_cost > 0 && (
                      <div>
                        <span>Cookie cost</span>
                        <strong
                          className="ml-1 text-amber-800"
                          data-testid={`profit-cookie-cost-${o.order_number}`}
                        >
                          −₹{Number(o.profit.launch_cookie_cost).toFixed(2)}
                        </strong>
                      </div>
                    )}
                    <div className="sm:border-l sm:border-emerald-800/20 sm:pl-5">
                      <span>Net profit</span>
                      <strong className="ml-1 text-emerald-800" data-testid={`profit-net-${o.order_number}`}>
                        {o.profit.net_profit === null
                          ? "Road route pending"
                          : `₹${Number(o.profit.net_profit).toFixed(2)}`}
                      </strong>
                    </div>
                  </div>
                  {o.fulfillment === "delivery" && o.profit.road_distance_km !== null && (
                    <p
                      className="mt-2 text-[11px] text-espresso-light"
                      data-testid={`profit-distance-${o.order_number}`}
                    >
                      Google road distance {Number(o.profit.road_distance_km).toFixed(2)} km ·
                      {" "}₹65 + ₹12/km after 3 km
                    </p>
                  )}
                  {(o.profit.box_cogs_details || []).map((box, boxIndex) => (
                    <div
                      key={`${box.product_id}-${boxIndex}`}
                      className="mt-3 border-t border-emerald-800/15 pt-3"
                      data-testid={`box-cogs-breakdown-${o.order_number}-${boxIndex}`}
                    >
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <span className="label-xs text-espresso-light">
                          {box.name} · discounted bundle revenue
                        </span>
                        <strong
                          className="text-xs text-espresso"
                          data-testid={`box-discounted-revenue-${o.order_number}-${boxIndex}`}
                        >
                          ₹{Number(box.final_revenue || 0).toFixed(2)}
                        </strong>
                      </div>
                      <div
                        className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-espresso-light"
                      >
                        {(box.flavor_cogs || []).map((flavor, flavorIndex) => (
                          <span
                            key={`${flavor.flavor}-${flavorIndex}`}
                            data-testid={
                              `box-flavor-cogs-${o.order_number}-${boxIndex}-${flavorIndex}`
                            }
                          >
                            {flavor.flavor} × {flavor.quantity} · {"₹"}
                            {Number(flavor.total_cogs || 0).toFixed(2)}
                          </span>
                        ))}
                      </div>
                      {box.is_cogs_high && (
                        <p
                          className="mt-2 text-xs text-amber-800"
                          data-testid={`box-cogs-warning-${o.order_number}-${boxIndex}`}
                        >
                          COGS is {Number(box.cogs_percent || 0).toFixed(1)}% of discounted
                          {" "}bundle revenue.
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-3 flex flex-wrap gap-3 items-center">
                <button
                  onClick={() => setDetailOrder(o)}
                  data-testid={`view-order-details-${o.order_number}`}
                  className="label-xs link-underline text-espresso"
                >
                  View details →
                </button>
                <button
                  onClick={() => setPrintingOrder(o)}
                  data-testid={`print-receipt-${o.order_number}`}
                  className="label-xs link-underline text-espresso"
                >
                  Print receipt →
                </button>
                <a
                  href={
                    `${process.env.REACT_APP_BACKEND_URL}/api/orders/`
                    + `${o.order_number}/invoice.pdf`
                  }
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`admin-download-gst-invoice-${o.order_number}`}
                  className="label-xs link-underline text-espresso"
                >
                  GST invoice ↓
                </a>
                {canDeleteOrders && (
                  <button
                    type="button"
                    onClick={() => setDeletingOrder(o)}
                    title="Delete order permanently"
                    aria-label={`Delete ${o.order_number} permanently`}
                    data-testid={`delete-order-${o.order_number}`}
                    className={
                      [
                        "inline-flex items-center justify-center gap-2",
                        "border border-rose-300 px-3 py-1.5",
                        "label-xs text-rose-700",
                        "transition-colors duration-200 hover:bg-rose-700 hover:text-white",
                      ].join(" ")
                    }
                  >
                    <Trash2 size={14} aria-hidden="true" />
                    <span>Delete</span>
                  </button>
                )}
                {o.status !== "cancelled" && o.status !== "delivered" && o.status !== "collected" && (
                  <button
                    onClick={() => setCancelingOrder(o)}
                    data-testid={`cancel-order-${o.order_number}`}
                    className="label-xs link-underline text-rose-600 hover:text-rose-700"
                  >
                    Cancel order →
                  </button>
                )}
                {o.status === "cancelled" && (
                  <span className="label-xs px-2 py-0.5 rounded-full border border-rose-300 text-rose-600 bg-rose-50" data-testid={`cancelled-badge-${o.order_number}`}>
                    Cancelled
                  </span>
                )}
                {o.print_status && (
                  <span
                    data-testid={`print-status-${o.order_number}`}
                    className={`label-xs px-2 py-0.5 rounded-full border ${
                      o.print_status === "submitted" ? "border-emerald-600 text-emerald-700 bg-emerald-50" :
                      o.print_status === "failed" ? "border-rose-500 text-rose-600 bg-rose-50" :
                      "border-line text-espresso-light"
                    }`}
                    title={o.print_job_id ? `PrintNode job #${o.print_job_id}` : ""}
                  >
                    Auto-print · {o.print_status}
                  </span>
                )}
              </div>
              {/* Never-dispatched delivery order — offer a manual dispatch
                  trigger so admin can rescue orders that missed the auto-fire
                  (e.g. after a Secrets fix, or a race at payment time). */}
              {o.fulfillment === "delivery" && o.payment_status === "paid" && o.status === "confirmed" && !o.carrier_order_id && (
                <div className="mt-3 flex items-center gap-3 text-xs bg-amber-50 border-l-2 border-amber-500 pl-3 py-2" data-testid={`carrier-missing-${o.order_number}`}>
                  <span className="text-amber-700">⚠ Not yet dispatched to uEngage{o.carrier_last_error ? ` · ${o.carrier_last_error}` : ""}</span>
                  <button
                    onClick={async () => {
                      if (!window.confirm("Dispatch this order to uEngage now?")) return;
                      try { await api.post(`/admin/orders/${o.id}/carrier/retry`); toast.success("Dispatch queued"); load(); }
                      catch(e){ toast.error(e?.response?.data?.detail || "Dispatch failed"); }
                    }}
                    data-testid={`carrier-dispatch-${o.order_number}`}
                    className="label-xs link-underline text-espresso"
                  >
                    Dispatch now →
                  </button>
                </div>
              )}
              {/* uEngage rider strip — shown once a task exists. Status pill
                  colors mirror the customer lifecycle so admins can eyeball
                  progress across a busy order list at a glance. */}
              {o.carrier_order_id && (
                <div
                  className="mt-3 pt-3 border-t border-line grid gap-3 sm:grid-cols-4 items-baseline"
                  data-testid={`uengage-strip-${o.order_number}`}
                >
                  <div>
                    <div className="label-xs text-espresso-light">uEngage AWB</div>
                    <div className="font-mono text-xs text-espresso mt-1 truncate" data-testid={`uengage-awb-${o.order_number}`}>
                      {o.carrier_awb || o.carrier_order_id}
                    </div>
                    {o.carrier_partner_name && (
                      <div className="text-xs text-espresso-light">via {o.carrier_partner_name}</div>
                    )}
                  </div>
                  <div>
                    <div className="label-xs text-espresso-light">Rider status</div>
                    <span
                      data-testid={`uengage-status-${o.order_number}`}
                      className={`inline-flex items-center label-xs mt-1 px-2 py-0.5 rounded-full border ${
                        o.carrier_status === "DELIVERED" ? "border-emerald-600 text-emerald-700 bg-emerald-50" :
                        o.carrier_status === "CANCELLED" || o.carrier_status === "RTO_COMPLETE" ? "border-rose-500 text-rose-600 bg-rose-50" :
                        (o.carrier_status || "").includes("ARRIVED") || o.carrier_status === "DISPATCHED" ? "border-gold text-gold-hover bg-ivory-100" :
                        "border-line text-espresso-light"
                      }`}
                    >
                      {o.carrier_status ? o.carrier_status.replaceAll("_", " ") : "—"}
                    </span>
                  </div>
                  <div>
                    <div className="label-xs text-espresso-light">Rider</div>
                    <div className="text-sm text-espresso mt-1 truncate">
                      {o.carrier_rider_name || <span className="text-espresso-light italic">unassigned</span>}
                    </div>
                    {o.carrier_rider_contact && (
                      <a
                        href={`tel:${o.carrier_rider_contact}`}
                        className="text-xs text-espresso-light link-underline"
                        data-testid={`uengage-rider-tel-${o.order_number}`}
                      >
                        {o.carrier_rider_contact}
                      </a>
                    )}
                  </div>
                  <div className="flex flex-col items-start gap-2">
                    {o.carrier_tracking_url && (
                      <a
                        href={o.carrier_tracking_url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`uengage-tracking-${o.order_number}`}
                        className="label-xs link-underline text-espresso"
                      >
                        Live map →
                      </a>
                    )}
                    <button
                      onClick={async () => {
                        try { await api.post(`/admin/orders/${o.id}/uengage/refresh`); toast.success("Rider status refreshed"); load(); }
                        catch(e){ toast.error(e?.response?.data?.detail || "Refresh failed"); }
                      }}
                      data-testid={`uengage-refresh-${o.order_number}`}
                      className="label-xs link-underline text-espresso-light"
                    >
                      Refresh
                    </button>
                  </div>
                </div>
              )}
              {o.kitchen_notes && (
                <div className="mt-3 p-3 bg-ivory-100 border-l-2 border-gold text-sm text-espresso italic" data-testid={`kitchen-notes-${o.order_number}`}>
                  <span className="label-xs text-gold-hover mr-2 not-italic">Kitchen note</span> {o.kitchen_notes}
                </div>
              )}
              {o.reward_applied && (
                <div className="mt-2 text-xs text-gold-hover">★ Loyalty reward redeemed</div>
              )}
              {o.referral_code_used && (
                <div className="mt-1 text-xs text-espresso-light">Referral: {o.referral_code_used}</div>
              )}
              {(o.status === "delivered" || o.status === "collected") && (
                <div className="mt-4 pt-4 border-t border-line flex flex-wrap items-baseline justify-between gap-3">
                  <div className="text-xs text-espresso-light">
                    {o.review_request_status === "sent" ? (
                      <>Review request sent{o.review_request_sent_at ? ` · ${fmtIST(o.review_request_sent_at)}` : ""}</>
                    ) : (
                      <>No review request sent yet.</>
                    )}
                  </div>
                  <button
                    onClick={() => sendReviewRequest(o)}
                    data-testid={`send-review-${o.order_number}`}
                    className="label-xs link-underline text-gold-hover"
                  >
                    {o.review_request_status === "sent" ? "Re-send review request →" : "Send review request now →"}
                  </button>
                </div>
              )}
              {o.razorpay_payment_id && o.payment_status !== "refunded" && (
                <div className="mt-3 pt-3 border-t border-line flex flex-wrap items-baseline justify-between gap-3">
                  <div className="text-xs text-espresso-light">
                    Payment · <span className="text-espresso">{o.razorpay_payment_id}</span>
                    {o.payment_status === "partially_refunded" && <span className="ml-2 text-gold-hover">· partially refunded</span>}
                  </div>
                  <button
                    onClick={() => refundOrder(o)}
                    data-testid={`refund-${o.order_number}`}
                    className="label-xs link-underline text-destructive"
                  >
                    Refund order →
                  </button>
                </div>
              )}
              {o.payment_status === "refunded" && (
                <div className="mt-3 text-xs text-gold-hover">↺ Refunded</div>
              )}
            </div>
          );
        })}
        {shown.length === 0 && <div className="text-espresso-light label-xs">No orders yet.</div>}
      </div>
      {printingOrder && (
        <ThermalReceipt order={printingOrder} onDone={() => setPrintingOrder(null)} />
      )}
      <OrderDetailsModal
        order={detailOrder}
        onClose={() => setDetailOrder(null)}
        onUpdated={updateOrderFromDetail}
      />
      <CancelOrderModal
        open={!!cancelingOrder}
        order={cancelingOrder}
        onClose={() => setCancelingOrder(null)}
        onCancelled={() => { setCancelingOrder(null); load(); }}
      />
      <DeleteOrderModal
        open={!!deletingOrder}
        order={deletingOrder}
        onClose={() => setDeletingOrder(null)}
        onDeleted={() => {
          setDeletingOrder(null);
          load();
        }}
      />
    </div>
  );
}

function ProductsPanel() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null); // null | product | "new"
  const load = useCallback(() => api.get("/products").then(({ data }) => setProducts(data)), []);
  useEffect(() => { load(); }, [load]);

  const toggleAvail = async (p) => {
    await api.put(`/admin/products/${p.id}`, { is_available: !p.is_available });
    load();
  };
  const del = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    await api.delete(`/admin/products/${p.id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div className="mt-8">
      <div className="flex justify-between items-center mb-6">
        <div className="label-xs text-espresso-light">{products.length} products</div>
        <Button onClick={() => setEditing("new")} data-testid="add-product-btn">Add product</Button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.map((p) => (
          <div key={p.id} className="border border-line p-5 bg-ivory">
            <div className="aspect-[4/3] bg-ivory-100 overflow-hidden mb-4">
              {p.image_url && <img src={cldImage(p.image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${p.image_url}` : p.image_url, { w: 500 })} alt={p.name} className="w-full h-full object-cover" />}
            </div>
            <div className="font-serif text-lg text-espresso">{p.name}</div>
            <div className="text-xs text-espresso-light">{p.category} · ₹{p.price}</div>
            <div className="flex justify-between items-center mt-3">
              <div className="flex items-center gap-2">
                <Switch checked={p.is_available} onCheckedChange={() => toggleAvail(p)} data-testid={`avail-${p.slug}`} />
                <span className="text-xs text-espresso-light">Available</span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditing(p)} className="text-xs link-underline" data-testid={`edit-${p.slug}`}>Edit</button>
                <button onClick={() => del(p)} className="text-xs text-destructive">Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {editing && <ProductEditor product={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ProductEditor({ product, onClose, onSaved }) {
  const isNew = !product;
  const [form, setForm] = useState(product || {
    slug: "", name: "", category: "cups", description: "", long_description: "",
    price: 0, image_url: "", flavor: "", is_available: true, is_featured: false, sort_order: 100,
  });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const save = async () => {
    try {
      if (isNew) {
        await api.post("/admin/products", form);
      } else {
        await api.put(`/admin/products/${product.id}`, form);
      }
      toast.success(isNew ? "Product created" : "Product updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div className="fixed inset-0 bg-espresso/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ivory border border-line max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
        <div className="label-xs text-espresso-light">{isNew ? "New product" : "Edit product"}</div>
        <h2 className="font-serif text-3xl text-espresso mt-2 mb-6">{form.name || "Untitled"}</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pe-name" /></div>
          <div><Label>Slug</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="pe-slug" /></div>
          <div><Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="pe-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cups">Cups</SelectItem>
                <SelectItem value="shakes">Shakes</SelectItem>
                <SelectItem value="tubs">Family Tubs</SelectItem>
                <SelectItem value="packs">Packs</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Price (₹)</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} data-testid="pe-price" /></div>
          <div><Label>Flavor</Label><Input value={form.flavor || ""} onChange={(e) => setForm({ ...form, flavor: e.target.value })} /></div>
          <div><Label>Sort order</Label><Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} /></div>
          <div className="md:col-span-2"><Label>Short description</Label><Input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div className="md:col-span-2"><Label>Long description</Label><Textarea rows={3} value={form.long_description || ""} onChange={(e) => setForm({ ...form, long_description: e.target.value })} /></div>
          <div className="md:col-span-2">
            <Label>Image</Label>
            <div className="flex gap-3 items-center">
              <Input value={form.image_url || ""} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="URL or upload" data-testid="pe-image-url" />
              <input type="file" accept="image/*" ref={fileRef} className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
              <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="pe-upload">{uploading ? "Uploading…" : "Upload"}</Button>
            </div>
          </div>
          <div className="flex items-center gap-3"><Switch checked={form.is_available} onCheckedChange={(v) => setForm({ ...form, is_available: v })} /><span className="text-sm">Available</span></div>
          <div className="flex items-center gap-3"><Switch checked={form.is_featured} onCheckedChange={(v) => setForm({ ...form, is_featured: v })} /><span className="text-sm">Featured</span></div>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} data-testid="pe-save">Save</Button>
        </div>
      </div>
    </div>
  );
}

function TimingsPanel() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/config/timings").then(({ data }) => setCfg(data)); }, []);
  if (!cfg) return <div className="mt-8 label-xs">Loading…</div>;
  const save = async () => { await api.put("/admin/config/timings", cfg); toast.success("Timings saved"); };
  return (
    <div className="mt-8 max-w-lg space-y-4">
      <div><Label>Open time (HH:MM)</Label><Input value={cfg.open_time} onChange={(e) => setCfg({ ...cfg, open_time: e.target.value })} /></div>
      <div><Label>Close time (HH:MM)</Label><Input value={cfg.close_time} onChange={(e) => setCfg({ ...cfg, close_time: e.target.value })} /></div>
      <div><Label>Slot interval (min)</Label><Input type="number" value={cfg.slot_interval_minutes} onChange={(e) => setCfg({ ...cfg, slot_interval_minutes: parseInt(e.target.value) || 30 })} /></div>
      <div className="flex items-center gap-3"><Switch checked={cfg.allow_asap} onCheckedChange={(v) => setCfg({ ...cfg, allow_asap: v })} /><span className="text-sm">Allow ASAP orders</span></div>
      <Button onClick={save} data-testid="timings-save">Save</Button>
    </div>
  );
}

function PromosPanel() {
  const [promos, setPromos] = useState([]);
  const [form, setForm] = useState({ code: "", type: "percent", value: 10, min_order_value: 0, is_active: true });
  const load = () => api.get("/admin/promotions").then(({ data }) => setPromos(data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    try {
      await api.post("/admin/promotions", { ...form, code: form.code.toUpperCase() });
      toast.success("Promo created"); setForm({ ...form, code: "", value: 10 }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { await api.delete(`/admin/promotions/${id}`); load(); };
  return (
    <div className="mt-8 grid md:grid-cols-2 gap-8">
      <div className="space-y-4">
        <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="promo-code" /></div>
        <div>
          <Label>Type</Label>
          <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="percent">Percent</SelectItem>
              <SelectItem value="fixed">Fixed (₹)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>Value ({form.type==="percent"?"%":"₹"})</Label><Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: parseFloat(e.target.value) || 0 })} /></div>
        <div><Label>Min order value (₹)</Label><Input type="number" value={form.min_order_value} onChange={(e) => setForm({ ...form, min_order_value: parseFloat(e.target.value) || 0 })} /></div>
        <Button onClick={create} data-testid="promo-create">Create promo</Button>
      </div>
      <div>
        <Label>Active promos</Label>
        <div className="mt-3 space-y-2">
          {promos.map((p) => (
            <div key={p.id} className="flex justify-between items-center border border-line px-4 py-3">
              <div>
                <div className="font-serif text-lg text-espresso">{p.code}</div>
                <div className="text-xs text-espresso-light">{p.type==="percent"?`${p.value}% off`:`₹${p.value} off`} · min ₹{p.min_order_value}</div>
              </div>
              <button onClick={() => del(p.id)} className="text-xs text-destructive">Delete</button>
            </div>
          ))}
          {promos.length === 0 && <div className="text-xs text-espresso-light">No promos yet.</div>}
        </div>
      </div>
    </div>
  );
}

function LoyaltyPanel() {
  const [data, setData] = useState(null);
  const [giftEmail, setGiftEmail] = useState("");
  const load = () => api.get("/admin/loyalty").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);
  const gift = async () => {
    if (!giftEmail) return;
    try {
      await api.post("/admin/loyalty/gift", { email: giftEmail });
      toast.success(`Free cup granted to ${giftEmail}`);
      setGiftEmail(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (!data) return <div className="mt-8 label-xs">Loading…</div>;
  const totals = data.totals || {};
  return (
    <div className="mt-8">
      <div className="grid md:grid-cols-4 gap-6">
        {[["Customers", totals.customers], ["Pending rewards", totals.pending], ["Redeemed", totals.redeemed], ["Referrals", totals.referrals]].map(([k, v]) => (
          <div key={k} className="border border-line p-6 bg-ivory">
            <div className="label-xs text-espresso-light">{k}</div>
            <div className="font-serif text-3xl text-espresso mt-2">{v ?? 0}</div>
          </div>
        ))}
      </div>

      <div className="mt-10 border border-gold/40 bg-gold/5 p-6 max-w-2xl">
        <div className="label-xs text-gold-hover">Gift a free cup</div>
        <div className="mt-2 text-sm text-espresso-light">Grant a VIP customer one free Traditional Italian — they'll see it at their next checkout.</div>
        <div className="mt-4 flex gap-3">
          <Input value={giftEmail} onChange={(e) => setGiftEmail(e.target.value)} placeholder="customer@example.com" data-testid="gift-email" />
          <Button onClick={gift} data-testid="gift-btn">Gift free cup</Button>
        </div>
      </div>

      <div className="mt-10 border border-line bg-ivory">
        <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr] gap-4 p-4 label-xs text-espresso-light border-b border-line">
          <div>Customer</div><div>Paid orders</div><div>Pending</div><div>Redeemed</div><div>Referrals</div><div>Referral code</div>
        </div>
        {(data.customers || []).map((c) => (
          <div key={c.email} className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr] gap-4 p-4 border-b border-line text-sm text-espresso" data-testid={`loyalty-row-${c.email}`}>
            <div>
              <div className="font-serif text-lg">{c.email}</div>
              <div className="text-xs text-espresso-light">{c.mobile || "—"}</div>
            </div>
            <div>{c.paid_count || 0}</div>
            <div className={c.pending_rewards > 0 ? "text-gold-hover font-medium" : ""}>{c.pending_rewards || 0}</div>
            <div>{c.redeemed_count || 0}</div>
            <div>{c.referral_count || 0}</div>
            <div className="font-mono text-xs">{c.referral_code}</div>
          </div>
        ))}
        {(data.customers || []).length === 0 && (
          <div className="p-8 text-espresso-light label-xs text-center">No customers yet. They'll appear here after their first paid order.</div>
        )}
      </div>
    </div>
  );
}


const BULK_STATUSES = ["new", "contacted", "quoted", "confirmed", "completed", "declined"];

function BulkPanel() {
  const [rows, setRows] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [quoting, setQuoting] = useState(null); // bulk id currently being quoted
  const [quoteForm, setQuoteForm] = useState({ price_per_cup: 249, delivery_fee: 0, total: 0, notes: "", valid_until: "" });
  const [sendingQuote, setSendingQuote] = useState(false);
  const load = () => api.get("/admin/bulk-orders").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.put(`/admin/bulk-orders/${id}/status`, { status });
    toast.success("Status updated");
    load();
  };

  const openQuote = (r) => {
    const existing = r.quote || {};
    const per = Number(existing.price_per_cup) || 249;
    const del = Number(existing.delivery_fee) || 0;
    setQuoteForm({
      price_per_cup: per,
      delivery_fee: del,
      total: Number(existing.total) || Math.round(per * r.quantity + del),
      notes: existing.notes || "",
      valid_until: existing.valid_until || "",
    });
    setQuoting(r.id);
  };

  const updateQuoteField = (key, val, r) => {
    setQuoteForm((f) => {
      const next = { ...f, [key]: val };
      if (key === "price_per_cup" || key === "delivery_fee") {
        const p = Number(key === "price_per_cup" ? val : next.price_per_cup) || 0;
        const d = Number(key === "delivery_fee" ? val : next.delivery_fee) || 0;
        next.total = Math.round(p * r.quantity + d);
      }
      return next;
    });
  };

  const sendQuote = async (r) => {
    setSendingQuote(true);
    try {
      const payload = {
        price_per_cup: Number(quoteForm.price_per_cup) || 0,
        delivery_fee: Number(quoteForm.delivery_fee) || 0,
        total: Number(quoteForm.total) || 0,
        notes: quoteForm.notes.trim() || null,
        valid_until: quoteForm.valid_until.trim() || null,
      };
      const { data } = await api.post(`/admin/bulk-orders/${r.id}/quote`, payload);
      toast.success("Quote emailed to customer");
      setQuoting(null);
      load();
      // Copy confirm URL to clipboard for convenience
      if (data.confirm_url && navigator.clipboard) {
        navigator.clipboard.writeText(data.confirm_url).catch(() => {});
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send quote");
    } finally {
      setSendingQuote(false);
    }
  };

  if (!rows) return <div className="mt-8 label-xs">Loading…</div>;

  return (
    <div className="mt-8">
      <div className="label-xs text-espresso-light mb-6">{rows.length} bulk request{rows.length === 1 ? "" : "s"}</div>
      <div className="space-y-4">
        {rows.map((r) => (
          <div key={r.id} className="border border-line bg-ivory" data-testid={`bulk-row-${r.reference}`}>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 p-4 md:p-6 items-start">
              <div>
                <div className="label-xs text-espresso-light">Reference</div>
                <div className="font-serif text-lg text-espresso mt-1">{r.reference}</div>
                <div className="text-xs text-espresso-light mt-1">{fmtIST(r.created_at)}</div>
              </div>
              <div>
                <div className="label-xs text-espresso-light">Occasion</div>
                <div className="text-sm text-espresso mt-1 capitalize">{r.occasion}{r.occasion_other ? ` — ${r.occasion_other}` : ""}</div>
              </div>
              <div>
                <div className="label-xs text-espresso-light">Quantity</div>
                <div className="font-serif text-2xl text-espresso mt-1">{r.quantity}</div>
              </div>
              <div>
                <div className="label-xs text-espresso-light">When · {r.fulfillment}</div>
                <div className="text-sm text-espresso mt-1">{r.date}{r.time ? ` · ${r.time}` : ""}</div>
              </div>
              <div>
                <div className="label-xs text-espresso-light">Contact</div>
                <div className="text-sm text-espresso mt-1">{r.customer.full_name}</div>
                <div className="text-xs text-espresso-light">{r.customer.mobile}</div>
                <div className="text-xs text-espresso-light">{r.customer.email}</div>
              </div>
              <div>
                <div className="label-xs text-espresso-light">Status</div>
                <select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)}
                  data-testid={`bulk-status-${r.reference}`}
                  className="mt-1 w-full bg-transparent border border-line px-2 py-1 text-sm text-espresso capitalize">
                  {BULK_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <div className="mt-2 flex gap-3">
                  <button
                    onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                    className="label-xs link-underline text-espresso"
                    data-testid={`bulk-expand-${r.reference}`}
                  >
                    {expanded === r.id ? "Hide" : "Details"}
                  </button>
                  <button
                    onClick={() => openQuote(r)}
                    className="label-xs link-underline text-gold-hover"
                    data-testid={`bulk-quote-open-${r.reference}`}
                  >
                    {r.quote ? "Re-send quote" : "Send quote"}
                  </button>
                </div>
              </div>
            </div>

            {expanded === r.id && (
              <div className="border-t border-line px-4 md:px-6 py-5 bg-ivory-100 grid md:grid-cols-2 gap-6 text-sm">
                <div>
                  <div className="label-xs text-espresso-light mb-2">Flavours{r.curated ? " · curated" : ""}</div>
                  <ul className="space-y-1 text-espresso">
                    {(r.selections || []).map((s, i) => (
                      <li key={i}><span className="text-gold mr-1">·</span>{s.flavor} × {s.qty}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  {r.fulfillment === "delivery" && (
                    <>
                      <div className="label-xs text-espresso-light mb-2">Delivery address</div>
                      <div className="text-espresso">
                        {r.customer.address_line1}{r.customer.address_line2 ? `, ${r.customer.address_line2}` : ""}<br/>
                        {r.customer.area}{r.customer.area ? ", " : ""}{r.customer.city} — {r.customer.pincode}<br/>
                        {r.customer.landmark && <span className="text-xs text-espresso-light">Landmark: {r.customer.landmark}</span>}
                      </div>
                    </>
                  )}
                  {r.notes && (
                    <div className="mt-4">
                      <div className="label-xs text-espresso-light mb-2">Customer notes</div>
                      <p className="italic text-espresso-light">{r.notes}</p>
                    </div>
                  )}
                  {r.quote && (
                    <div className="mt-4 border-l-2 border-gold pl-3">
                      <div className="label-xs text-gold-hover mb-2">Last quote sent</div>
                      <div className="text-espresso">₹{r.quote.price_per_cup}/cup · Total ₹{r.quote.total}</div>
                      <div className="text-xs text-espresso-light mt-1">{fmtIST(r.quote.sent_at)}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {quoting === r.id && (
              <div className="border-t border-line px-6 py-6 bg-gold/5" data-testid={`bulk-quote-form-${r.reference}`}>
                <div className="flex items-baseline justify-between">
                  <div className="label-xs text-gold-hover">Send a quote — {r.reference}</div>
                  <button onClick={() => setQuoting(null)} className="label-xs text-espresso-light hover:text-espresso">Cancel</button>
                </div>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label>Price / cup (₹)</Label>
                    <Input type="number" min={0} value={quoteForm.price_per_cup}
                      onChange={(e) => updateQuoteField("price_per_cup", e.target.value, r)}
                      data-testid={`quote-price-${r.reference}`} />
                  </div>
                  <div>
                    <Label>Delivery / handling (₹)</Label>
                    <Input type="number" min={0} value={quoteForm.delivery_fee}
                      onChange={(e) => updateQuoteField("delivery_fee", e.target.value, r)}
                      data-testid={`quote-delivery-${r.reference}`} />
                  </div>
                  <div>
                    <Label>Total (₹)</Label>
                    <Input type="number" min={0} value={quoteForm.total}
                      onChange={(e) => setQuoteForm((f) => ({ ...f, total: e.target.value }))}
                      data-testid={`quote-total-${r.reference}`} />
                    <div className="text-xs text-espresso-light mt-1">{r.quantity} cups × ₹{quoteForm.price_per_cup} + ₹{quoteForm.delivery_fee}</div>
                  </div>
                  <div>
                    <Label>Valid until (optional)</Label>
                    <Input type="date" value={quoteForm.valid_until}
                      onChange={(e) => setQuoteForm((f) => ({ ...f, valid_until: e.target.value }))} />
                  </div>
                  <div className="md:col-span-4">
                    <Label>A note for the customer (optional)</Label>
                    <Textarea rows={3} value={quoteForm.notes}
                      onChange={(e) => setQuoteForm((f) => ({ ...f, notes: e.target.value }))}
                      placeholder="e.g. Delivered chilled by 11:00 AM. Custom kraft sleeves with your logo included."
                      data-testid={`quote-notes-${r.reference}`} />
                  </div>
                </div>
                <div className="mt-5 flex items-center justify-between">
                  <div className="text-xs text-espresso-light italic">
                    We'll email a boutique quote card with a one-tap Confirm link. Confirm URL auto-copied on send.
                  </div>
                  <Button onClick={() => sendQuote(r)} disabled={sendingQuote} data-testid={`quote-send-${r.reference}`}>
                    {sendingQuote ? "Sending…" : "Send quote"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
        {rows.length === 0 && <div className="text-espresso-light label-xs">No bulk requests yet.</div>}
      </div>
    </div>
  );
}


const REVIEW_STATUSES = ["pending", "approved", "rejected", "hidden"];
const PHOTO_STATUSES  = ["pending", "approved", "rejected"];

function StarRow({ n }) {
  return (
    <span className="inline-flex gap-0.5 text-gold">
      {[1,2,3,4,5].map((i) => <span key={i}>{i <= n ? "★" : "☆"}</span>)}
    </span>
  );
}

function ReviewsPanel() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [expanded, setExpanded] = useState(null);

  const load = () => api.get("/admin/reviews", { params: filter ? { status: filter } : {} }).then(({ data }) => setRows(data));
  useEffect(() => { load(); }, [filter]);

  const update = async (id, patch, msg) => {
    await api.put(`/admin/reviews/${id}`, patch);
    toast.success(msg || "Updated");
    load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    await api.delete(`/admin/reviews/${id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div className="mt-8">
      <div className="flex flex-wrap gap-2 mb-6">
        {["pending","approved","rejected","hidden",""].map((f) => (
          <button key={f||"all"} onClick={() => setFilter(f)} data-testid={`reviews-filter-${f||"all"}`}
            className={`label-xs px-3 py-1 rounded-full border capitalize ${filter===f?"bg-espresso text-ivory border-espresso":"border-line text-espresso-light"}`}>
            {f || "All"}
          </button>
        ))}
      </div>
      <div className="space-y-4">
        {rows.map((r) => {
          const photoSrc = r.photo_url ? (r.photo_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${r.photo_url}` : r.photo_url) : null;
          return (
            <div key={r.id} className="border border-line bg-ivory" data-testid={`review-row-${r.id}`}>
              <div className="grid md:grid-cols-6 gap-4 p-6 items-start">
                <div>
                  <div className="label-xs text-espresso-light">Product</div>
                  <div className="font-serif text-lg text-espresso mt-1">{r.product_name}</div>
                  <div className="text-xs text-espresso-light mt-1">{r.order_number}</div>
                </div>
                <div>
                  <div className="label-xs text-espresso-light">Customer</div>
                  <div className="text-sm text-espresso mt-1">{r.customer_name}</div>
                  <div className="text-xs text-espresso-light">{r.customer_email}</div>
                  {r.verified_purchase && <div className="text-xs text-gold-hover mt-1">✓ Verified Purchase</div>}
                </div>
                <div>
                  <div className="label-xs text-espresso-light">Rating</div>
                  <div className="mt-1"><StarRow n={r.rating} /></div>
                  <div className="text-xs text-espresso-light mt-1">{fmtIST(r.created_at)}</div>
                </div>
                <div>
                  <div className="label-xs text-espresso-light">Text status</div>
                  <select value={r.status} onChange={(e)=>update(r.id, {status: e.target.value}, `Marked ${e.target.value}`)}
                    data-testid={`review-status-${r.id}`}
                    className="mt-1 w-full bg-transparent border border-line px-2 py-1 text-sm text-espresso capitalize">
                    {REVIEW_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <div className="label-xs text-espresso-light">Photo</div>
                  {r.photo_url ? (
                    <>
                      <select value={r.photo_status || "pending"} onChange={(e)=>update(r.id, {photo_status: e.target.value}, "Photo updated")}
                        data-testid={`photo-status-${r.id}`}
                        className="mt-1 w-full bg-transparent border border-line px-2 py-1 text-sm text-espresso capitalize">
                        {PHOTO_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <button onClick={()=>setExpanded(expanded===r.id?null:r.id)} className="mt-2 label-xs link-underline text-espresso" data-testid={`review-expand-${r.id}`}>
                        {expanded===r.id?"Hide":"View photo"}
                      </button>
                    </>
                  ) : (
                    <div className="text-xs text-espresso-light mt-1">—</div>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  {r.status !== "approved" && (
                    <button onClick={()=>update(r.id, {status: "approved"}, "Approved")} data-testid={`approve-${r.id}`}
                      className="label-xs px-3 py-1 border border-gold text-gold-hover hover:bg-gold hover:text-ivory transition-colors">
                      Approve
                    </button>
                  )}
                  {r.status !== "rejected" && (
                    <button onClick={()=>update(r.id, {status: "rejected"}, "Rejected")}
                      className="label-xs px-3 py-1 border border-line text-espresso-light hover:border-espresso hover:text-espresso transition-colors">
                      Reject
                    </button>
                  )}
                  <button onClick={()=>del(r.id)} className="label-xs text-destructive hover:opacity-80">Delete</button>
                </div>
              </div>

              {(r.text || expanded===r.id) && (
                <div className="border-t border-line px-6 py-5 bg-ivory-100 grid md:grid-cols-3 gap-6">
                  {r.text && (
                    <div className="md:col-span-2">
                      <div className="label-xs text-espresso-light mb-2">Review text</div>
                      <p className="italic font-serif text-espresso leading-relaxed">"{r.text}"</p>
                      <div className="mt-4 flex flex-wrap gap-4 text-xs text-espresso-light">
                        {r.sweetness && <span><span className="label-xs text-espresso-light">Sweetness</span> · <span className="text-espresso">{r.sweetness.replace(/_/g," ")}</span></span>}
                        {r.texture && <span><span className="label-xs text-espresso-light">Texture</span> · <span className="text-espresso">{r.texture.replace(/_/g," ")}</span></span>}
                        {r.would_order_again && <span><span className="label-xs text-espresso-light">Reorder</span> · <span className="text-espresso">{r.would_order_again.replace(/_/g," ")}</span></span>}
                      </div>
                    </div>
                  )}
                  {expanded===r.id && photoSrc && (
                    <div>
                      <div className="label-xs text-espresso-light mb-2">Photo (status: {r.photo_status})</div>
                      <img src={photoSrc} alt="Customer" className="max-h-56 object-contain border border-line" />
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {rows.length === 0 && <div className="text-espresso-light label-xs">No reviews in this bucket.</div>}
      </div>
    </div>
  );
}
